/**
 * Operator copy for each mission dock tab.
 *
 * Kept out of the icon so it can be tested, and so a tab can reuse the same
 * text whether the icon sits on the tab strip or next to the page title.
 */
export type DockHelpId =
  | 'sentinel'
  | 'activity'
  | 'chat'
  | 'follow'
  | 'signals'
  | 'photos'
  | 'dwells'
  | 'areas'
  | 'line'
  | 'along'
  | 'report';

export interface DockHelpItem {
  label: string;
  text: string;
}

export interface DockTabHelpCopy {
  title: string;
  lead: string;
  items?: DockHelpItem[];
}

export const DOCK_TAB_HELP: Record<DockHelpId, DockTabHelpCopy> = {
  sentinel: {
    title: 'Sentinel',
    lead: 'Ask the mission AI while the run is live. The strip is the current threat level. Findings you accept live on Report, not here.',
    items: [
      {
        label: 'Ask',
        text: 'Route, followers, weather, and what is ahead. Sentinel reads this mission, not the open web unless you send it there.',
      },
      {
        label: 'Strip',
        text: 'Clear / watch / elevated / high / critical. The reason line is why the level is where it is.',
      },
      {
        label: 'Not this tab',
        text: 'Crew talk is Chat. The write-up is Report. Live video is a thumbnail on the map — enlarge it there.',
      },
    ],
  },
  activity: {
    title: 'Activity',
    lead: 'Time-ordered alerts for this run. The badge is open items that still need a look, not the whole history.',
    items: [
      {
        label: 'Open',
        text: 'Still standing. Ack all clears the queue once you have seen it.',
      },
      {
        label: 'History',
        text: 'What fired on this run, including what you already acknowledged. Earlier runs stay on Report.',
      },
      {
        label: 'Drill',
        text: 'A gold banner means the feed is a baseline run, not a live threat.',
      },
    ],
  },
  chat: {
    title: 'Chat',
    lead: 'The same #general thread as the phones. People in the vehicle — not Sentinel.',
    items: [
      {
        label: 'Send',
        text: 'Notes and photos reach the crew on the phones and this dock.',
      },
      {
        label: 'Alerts',
        text: 'Vehicle lines show up here too. Status flips are filtered so the pane stays talk.',
      },
      {
        label: 'Live',
        text: 'Green is a live link. Polling still delivers, just a few seconds slower.',
      },
    ],
  },
  follow: {
    title: 'Follows',
    lead: 'Radios that stay with the convoy on the planned route. Traffic that shares ten minutes of road is not a follow. Distance together is what traffic cannot fake.',
    items: [
      {
        label: 'Watching',
        text: 'Heard near us. Not enough shared segments, time, and distance yet.',
      },
      {
        label: 'Suspected',
        text: 'Shared enough of the route across separate minutes to be more than coincidence.',
      },
      {
        label: 'Confirmed',
        text: 'Three high-score ticks in a row — or already on the watchlist and travelling with us. Watchlist skips the ladder.',
      },
      {
        label: 'Following',
        text: 'Confirmed and still with us after about eight miles. Entrenched is ten.',
      },
      {
        label: 'Lost',
        text: 'Went quiet. A new radio sitting where a confirmed follower dropped can inherit that status (gap fill).',
      },
      {
        label: 'Watchlist',
        text: 'Promote a row to keep it. A known-hostile identifier that is also with us confirms immediately. Watchlisted rows stay pressing.',
      },
      {
        label: 'Reroute',
        text: 'Still with us after the route changed means they are following us, not the road.',
      },
      {
        label: 'Own kit',
        text: 'Scan and exclude our radios so they do not look like a tail.',
      },
      {
        label: 'All',
        text: 'Current is this run. All keeps followers from earlier runs of this mission, including after a new run cleared the live ladder.',
      },
    ],
  },
  signals: {
    title: 'Signals',
    lead: 'What the observers heard around the convoy. A digest, not a follow list — that is Follows.',
    items: [
      {
        label: 'Scan',
        text: 'While the mission is running this refreshes every five minutes. Scan now takes a fresh digest.',
      },
      {
        label: 'Heard',
        text: 'Every emitter in the last digest, including own kit and one-off hits. Search by name or MAC. Gold ticks mean two or more distinct places.',
      },
      {
        label: 'Watchlist',
        text: 'Promote a row if you want it remembered after this run.',
      },
    ],
  },
  photos: {
    title: 'Photos',
    lead: 'Field reports from this run. Setup shots stay on the file until a run starts. Each card is a numbered FR — what was seen, what was assessed, and where.',
    items: [
      {
        label: 'Original / Identified',
        text: 'The capture next to the annotated frame when DataHog has finished.',
      },
      {
        label: 'Promote',
        text: 'Lift a report into an intel packet. Link ties two reports on the same subject.',
      },
      {
        label: 'Re-look',
        text: 'Ask the vehicle to look again at that spot. Mission must be running.',
      },
    ],
  },
  dwells: {
    title: 'Dwells',
    lead: 'Places someone actually lingered. Tracks smear a stop into a line; this is the stay. Not a threat list.',
    items: [
      {
        label: 'Our kit',
        text: 'Assigned devices that stopped. First because it is our movement.',
      },
      {
        label: 'Observed',
        text: 'Someone else who stayed on the planned line in the same window.',
      },
      {
        label: 'Duration',
        text: 'Stops longer than about two minutes. The bar is sized against the longest stay on this run.',
      },
    ],
  },
  areas: {
    title: 'Fences',
    lead: 'Who is inside the watched areas. Presence, not a heartbeat log.',
    items: [
      {
        label: 'Occupancy',
        text: 'Enter ticks while someone stays put are one visit. An exit closes it.',
      },
      {
        label: 'Watchlist',
        text: 'Watchlisted occupants pin above ordinary stays so a long visit cannot bury them.',
      },
      {
        label: 'Watch / Draw',
        text: 'Attach an existing geofence or draw a new one on the mission map.',
      },
    ],
  },
  line: {
    title: 'Route',
    lead: 'The plan you are on: named routes, turn-by-turn, and corridor risk. Switch the active plan here.',
    items: [
      {
        label: 'Named routes',
        text: 'Primary, Secondary, and the rest. The card marks which plan is ACTIVE.',
      },
      {
        label: 'Corridor',
        text: 'Risk on the line itself — not a hospital nearby. That lives under Info.',
      },
      {
        label: 'Reroute',
        text: 'Emergency reroute is under the last named route. A follower who survives it is following us.',
      },
    ],
  },
  along: {
    title: 'Info',
    lead: 'What is along the route: places you may need, events that change a plan, and weather.',
    items: [
      {
        label: 'Places',
        text: 'Hospitals, fuel, police — services, not threats.',
      },
      {
        label: 'Events',
        text: 'News and disruptions in the corridor. Check the source before acting.',
      },
      {
        label: 'Weather',
        text: 'Forecast on the line for the window you are driving.',
      },
    ],
  },
  report: {
    title: 'Report',
    lead: 'Sentinel findings and the run write-up. Drafts stay drafts until you accept them.',
    items: [
      {
        label: 'Draft findings',
        text: 'Ask Sentinel to read the run. Accept what is true; reject the rest.',
      },
      {
        label: 'Write-up',
        text: 'The after-action lives here so opening chat does not bury it.',
      },
    ],
  },
};
